import React from 'react';

import { Box } from '@mui/material';

export type LensCrossFilterKey = {
  column: string;
  value: string;
  label: string;
  active: boolean;
};

type Props = {
  keys: ReadonlyArray<LensCrossFilterKey>;
  ariaLabel?: string;
  onSelect: (key: LensCrossFilterKey) => void;
};

/**
 * A canvas has no per-mark node to hang role="button" on, so the marks a chart
 * can be filtered by are published here instead: one real button each, named,
 * with aria-pressed. Off-screen until something in it takes focus, at which
 * point it surfaces so a sighted keyboard user can see where they are.
 */
export default function LensCrossFilterKeys({ keys, onSelect, ariaLabel = 'Filter by a value in this chart' }: Props) {
  if (!keys.length) {
    return null;
  }

  return (
    <Box
      aria-label={ariaLabel}
      component="ul"
      sx={{
        clip: 'rect(0 0 0 0)',
        clipPath: 'inset(50%)',
        // '1px', not 1. In MUI's sx a bare number between 0 and 1 on width or height is a
        // fraction, so `height: 1` compiles to `height: 100%`. Absolutely positioned with no
        // inset, this list stayed at its static position after the chart and then ran a full
        // container height below it, ending 165px past the card's own edge.
        height: '1px',
        listStyle: 'none',
        m: 0,
        overflow: 'hidden',
        p: 0,
        position: 'absolute',
        whiteSpace: 'nowrap',
        width: '1px',
        '&:focus-within': {
          backgroundColor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          clip: 'auto',
          clipPath: 'none',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.5,
          height: 'auto',
          inset: 'auto 4px 4px 4px',
          maxHeight: '60%',
          overflowY: 'auto',
          p: 0.5,
          whiteSpace: 'normal',
          width: 'auto',
          zIndex: 2,
        },
      }}
    >
      {keys.map((key) => (
        <li key={`${key.column}:${key.value}`}>
          <Box
            aria-pressed={key.active}
            component="button"
            onClick={() => onSelect(key)}
            sx={{
              backgroundColor: key.active ? 'action.selected' : 'transparent',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              color: 'text.primary',
              cursor: 'pointer',
              font: 'inherit',
              fontSize: 12,
              px: 0.75,
              py: 0.25,
            }}
            type="button"
          >
            {key.label}
          </Box>
        </li>
      ))}
    </Box>
  );
}
